const ACTIVE_KYC_STATUSES = new Set([
  'pending',
  'provider_pending',
  'manual_review',
  'requested',
  'processing',
  'submitted',
]);

const RETRYABLE_KYC_STATUSES = new Set([
  'rejected',
  'refused',
  'denied',
  'invalid',
  'error',
  'failed',
  'expired',
]);

const DOCUMENT_LABELS = {
  selfie: 'Selfie',
  document_front: 'Documento frente',
  document_back: 'Documento verso',
  document_summary: 'Resumo documental',
  provider_receipt: 'Recibo provider',
  consent_proof: 'Prova de consentimento',
};

const ATTEMPT_LABELS = {
  identity_request: 'Solicitação de identidade',
  provider_response: 'Resposta do provider',
  provider_callback: 'Callback provider',
  credit_request: 'Consulta de crédito',
  document_intake: 'Entrada de documento',
  manual_review: 'Revisão manual',
};

const STATUS_LABELS = {
  pending: 'Em análise',
  provider_pending: 'Em análise',
  manual_review: 'Revisão manual',
  approved: 'Aprovado',
  rejected: 'Reprovado',
  refused: 'Reprovado',
  denied: 'Reprovado',
  invalid: 'Inválido',
  error: 'Erro',
  failed: 'Falhou',
  expired: 'Expirado',
  requested: 'Solicitado',
  processing: 'Processando',
  submitted: 'Enviado',
  succeeded: 'Concluído',
  callback_accepted: 'Callback aceito',
  callback_ignored: 'Callback ignorado',
};

const SENSITIVE_KEY_PATTERN =
  /token|secret|authorization|sign_url|signed_url|url|link|base64|raw|image|selfie|document_bytes|file_content/i;

export const asArray = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

export const kycStatusLabel = status =>
  STATUS_LABELS[String(status || '').toLowerCase()] || status || 'Não enviado';

export const kycStatusClass = status => {
  const token = String(status || '').toLowerCase();
  if (['approved', 'succeeded'].includes(token)) {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (ACTIVE_KYC_STATUSES.has(token)) return 'bg-amber-50 text-amber-700';
  if (RETRYABLE_KYC_STATUSES.has(token)) return 'bg-rose-50 text-rose-700';
  return 'bg-lais-bg-soft text-lais-text-muted';
};

export const kycVerificationsForOffer = offer => {
  const verifications = [
    ...asArray(offer?.kyc_verifications),
    ...asArray(offer?.kyc_verification),
  ];
  const seen = new Set();
  return verifications.filter(verification => {
    const key =
      verification?.id ||
      `${verification?.status || 'unknown'}-${verification?.created_at || ''}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export function allAttemptsForVerification(verification) {
  const attempts = [
    ...asArray(verification?.attempts),
    ...asArray(verification?.verification_attempts),
    ...asArray(verification?.identity_verifications).flatMap(identity => [
      identity?.latest_attempt,
      ...asArray(identity?.attempts),
    ]),
  ];
  const seen = new Set();
  return attempts.filter(attempt => {
    const key =
      attempt?.id ||
      `${attempt?.attempt_kind || ''}-${attempt?.status || ''}-${
        attempt?.occurred_at || ''
      }`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export const isActiveKycVerification = verification => {
  if (!verification) return false;
  if (verification.active === true) return true;
  const status = String(verification.status || '').toLowerCase();
  if (ACTIVE_KYC_STATUSES.has(status)) return true;
  return allAttemptsForVerification(verification).some(attempt =>
    ACTIVE_KYC_STATUSES.has(String(attempt?.status || '').toLowerCase())
  );
};

export const activeKycVerificationForOffer = offer =>
  kycVerificationsForOffer(offer).find(isActiveKycVerification) || null;

export const canCreateKycVerificationForOffer = offer =>
  !activeKycVerificationForOffer(offer);

export const isRetryableKycVerification = verification => {
  if (!verification) return true;
  if (verification.retryable === true) return true;
  return RETRYABLE_KYC_STATUSES.has(
    String(verification.status || '').toLowerCase()
  );
};

export const allDocumentsForVerification = verification => {
  const documents = [
    ...asArray(verification?.documents),
    ...asArray(verification?.document_artifacts),
    ...asArray(verification?.identity_verifications).flatMap(identity =>
      asArray(identity?.documents)
    ),
  ];
  const seen = new Set();
  return documents.filter(document => {
    const key =
      document?.id ||
      `${document?.artifact_kind || document?.kind || ''}-${
        document?.content_sha256 || ''
      }`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const allRiskDecisionsForVerification = verification => {
  const decisions = [
    verification?.latest_risk_decision,
    ...asArray(verification?.risk_decisions),
    ...asArray(verification?.identity_verifications).flatMap(identity => [
      identity?.latest_risk_decision,
      ...asArray(identity?.risk_decisions),
    ]),
  ];
  const seen = new Set();
  return decisions.filter(decision => {
    const key =
      decision?.id ||
      `${decision?.decision || ''}-${decision?.decision_reason || ''}-${
        decision?.issued_at || ''
      }`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const kycResponsibleLabel = verification => {
  const attempt = allAttemptsForVerification(verification).find(
    item => item?.actor?.name || item?.actor_id
  );
  if (attempt?.actor?.name) return attempt.actor.name;
  if (attempt?.actor_id) return `Operador #${attempt.actor_id}`;

  const identity = asArray(verification?.identity_verifications).find(
    item => item?.requested_by?.name || item?.requested_by_id
  );
  if (identity?.requested_by?.name) return identity.requested_by.name;
  if (identity?.requested_by_id) return `Operador #${identity.requested_by_id}`;
  return 'Automação KYC';
};

export const kycRejectionReason = verification => {
  const directReason =
    verification?.rejection_reason ||
    verification?.failure_reason ||
    verification?.result?.rejection_reason ||
    verification?.result?.reason ||
    verification?.result?.error_message ||
    verification?.result?.message ||
    verification?.metadata?.kyc_rejection_reason_label ||
    verification?.metadata?.kyc_rejection_reason ||
    verification?.metadata?.documentation_rejection_reason_label ||
    verification?.metadata?.documentation_rejection_reason ||
    verification?.metadata?.last_error;
  if (directReason) return directReason;

  const riskDecision = allRiskDecisionsForVerification(verification).find(
    item => item?.decision_reason
  );
  if (riskDecision?.decision_reason) return riskDecision.decision_reason;

  const failedAttempt = allAttemptsForVerification(verification).find(
    item => item?.error_message_redacted || item?.error_code
  );
  return (
    failedAttempt?.error_message_redacted || failedAttempt?.error_code || ''
  );
};

export const kycDocumentRows = verification =>
  allDocumentsForVerification(verification).map((document, index) => {
    const kind =
      document?.artifact_kind || document?.kind || 'document_summary';
    const hash = document?.content_sha256 || document?.sha256 || '';
    return {
      key: document?.id || `${kind}-${index}`,
      label: DOCUMENT_LABELS[kind] || kind,
      status: kycStatusLabel(document?.storage_state || 'redacted'),
      storageState: document?.storage_state || 'redacted',
      hashLabel: hash ? `${hash.slice(0, 12)}…` : 'Hash indisponível',
      filename: document?.metadata?.filename || '',
      redacted: document?.redacted !== false,
    };
  });

export const kycAttemptRows = verification =>
  allAttemptsForVerification(verification).map((attempt, index) => ({
    key: attempt?.id || `${attempt?.attempt_kind || 'attempt'}-${index}`,
    label:
      ATTEMPT_LABELS[attempt?.attempt_kind] ||
      attempt?.attempt_kind ||
      `Tentativa ${index + 1}`,
    status: kycStatusLabel(attempt?.status),
    statusClass: kycStatusClass(attempt?.status),
    provider: attempt?.provider || verification?.provider || 'provider',
    occurredAt: attempt?.occurred_at || '',
    responsible:
      attempt?.actor?.name ||
      (attempt?.actor_id ? `Operador #${attempt.actor_id}` : ''),
    error: attempt?.error_message_redacted || attempt?.error_code || '',
    redacted: attempt?.redacted !== false,
  }));

const safeScalar = value =>
  ['string', 'number', 'boolean'].includes(typeof value) ? String(value) : '';

const redactedEvidenceEntries = (payload, prefix = '') => {
  if (!payload || typeof payload !== 'object') return [];
  return Object.entries(payload).flatMap(([key, value]) => {
    const label = prefix ? `${prefix}.${key}` : key;
    if (SENSITIVE_KEY_PATTERN.test(label)) {
      return [{ label, value: '[redigido]' }];
    }
    const scalar = safeScalar(value);
    if (scalar) return [{ label, value: scalar }];
    if (Array.isArray(value)) {
      return [{ label, value: `${value.length} item(ns) redigidos` }];
    }
    return redactedEvidenceEntries(value, label);
  });
};

export const kycEvidenceRows = verification => {
  const rows = [
    ...redactedEvidenceEntries(verification?.result),
    ...redactedEvidenceEntries(verification?.metadata),
  ];
  const seen = new Set();
  return rows
    .filter(row => {
      const key = `${row.label}-${row.value}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 8);
};

export const kycVerificationSummaryRows = verification =>
  [
    {
      label: 'Documento',
      value: verification?.document_type || 'Não informado',
    },
    {
      label: 'Responsável',
      value: kycResponsibleLabel(verification),
    },
    {
      label: 'Provider',
      value: verification?.provider || 'Não informado',
    },
    {
      label: 'Retenção',
      value: verification?.retention_until || '',
      date: true,
    },
  ].filter(row => row.value);
